<template>
  <div
    class="main-product critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div
      v-if="isMobile"
      class="main-product__details"
    >
      <div
        class="main-product__breadcrumbs"
        v-html="breadcrumbs"
      />

      <h1
        id="template-title"
        class="
          main-product__title
          text-heading-l
          text-heading-2xl-desktop
        "
        v-text="product.title || liquid.title"
      />

      <div class="main-product__prices-and-yotpo-container">
        <product-prices
          class="main-product__prices-container"
          class-price="text-body-m-bold text-heading-l-desktop"
          :available="variant.available"
          :compare-at-price="variant.compareAtPrice?.amount"
          :price="variant.price?.amount || liquid.selectedOrFirstAvailableVariant?.price?.amount"
          :varies="variant.price?.varies || liquid.selectedOrFirstAvailableVariant?.price?.varies"
        />

        <div ref="yotpoStarRatingPlaceholder" />
      </div>
    </div>

    <div class="grid">
      <div
        id="product-gallery"
        class="main-product__gallery-container col xs-span l6"
        data-sign-post-location="main"
        js-sign-post
      >
        <product-gallery
          class="main-product__gallery"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          layout="tabs-grid"
          :product="product || liquid"
          :variant="variant"
          :alternate-columns="settings.alternateColumns"
          @update-model-info="handleModelInfoUpdate"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </div>

      <div
        class="main-product__details-container col xs-span l6"
        :class="[
          {
            'is-sticky': settings.stickyProductForm
          }
        ]"
      >
        <div
          v-if="!isMobile"
          class="main-product__details"
        >
          <div
            class="main-product__breadcrumbs"
            v-html="breadcrumbs"
          />

          <h1
            id="template-title"
            class="
              main-product__title
              text-heading-l
              text-heading-2xl-desktop
            "
            v-text="product.title || liquid.title"
          />

          <div class="main-product__prices-and-yotpo-container">
            <product-prices
              class="main-product__prices-container"
              class-price="text-body-m-bold text-heading-l-desktop"
              :available="variant.available"
              :compare-at-price="variant.compareAtPrice?.amount"
              :price="
                variant.price?.amount ||
                  liquid.selectedOrFirstAvailableVariant?.price?.amount
              "
              :varies="
                variant.price?.varies ||
                  liquid.selectedOrFirstAvailableVariant?.price?.varies
              "
            />

            <div ref="yotpoStarRatingPlaceholder" />
          </div>
        </div>

        <product-colour-selector
          :product="product || liquid"
          :related-products="relatedCollection.products"
          :variant="variant"
          :back-in-stock="settings.backInStock"
        />

        <variant-carousel
          :product="product || liquid"
          :related-products="relatedCollection.products"
          :variant="variant"
          @variant-query="handleVariantQuery"
        />

        <p
          v-if="modelInfo"
          class="main-product__model-details text-body-xs-regular text-body-s-regular-desktop"
          v-text="modelInfo"
        />

        <div
          v-if="product.alternateProducts"
          class="main-product__alternate-products__container"
        >
          <p
            class="text-body-xs-bold text-body-s-bold-desktop"
            v-text="product.alternateProducts?.title"
          />

          <div class="main-product__alternate-products__swatches">
            <a
              v-for="option of product.alternateProducts?.options"
              :key="option.url"
              class="
                main-product__alternate-products__swatch
                text-body-s-regular
              "
              :class="[
                {
                  [$class('active')]: option.url?.split('/').at(-1) === product.handle
                }
              ]"
              :href="option.url"
              v-text="option.name"
            />
          </div>
        </div>

        <product-form
          class="main-product__form"
          :product="product || liquid"
          :variant="variant"
          :quantity-selector="false"
          :size-explainer="settings.sizeExplainer"
          :low-in-stock="settings.lowInStock"
          :default-message="settings.message"
          :shipping="settings.shipping"
          :back-in-stock="settings.backInStock"
          :ab-test="abTest"
          :model-info="computedModelInfo"
          :related-collection="relatedCollection"
          :payment-icons="settings.paymentIcons"
          :show-unit-switcher="settings.showUnitSwitcher"
          @update-variant="handleVariantUpdate"
        />

        <div
          v-if="settings.charity.enabled && settings.showRnli"
          class="main-product__charity-donation"
        >
          <div
            v-if="settings.charity.icon?.src?.length"
            class="main-product__charity-logo"
          >
            <responsive-image
              :image="settings.charity.icon"
            />
          </div>

          <div class="main-product__charity-content">
            <p
              class="text-body-xs-regular text-body-s-regular-desktop"
              v-text="settings.charity.message"
            />

            <transition-height>
              <p
                v-show="showCharityDetails"
                class="text-body-xs-regular text-body-s-regular-desktop"
                aria-live="polite"
                v-text="$string(
                  'product.charity_donation.read_more_text',
                  {
                    replace: { amount: settings.charity.amount }
                  }
                )"
              />
            </transition-height>

            <button
              type="button"
              class="
              main-product__charity-toggle
                text-body-xs-underline
                text-body-s-underline-desktop
              "
              :aria-pressed="showCharityDetails"
              @click="showCharityDetails = !showCharityDetails"
              v-text="showCharityDetails
                ? $string('product.charity_donation.read_less_link')
                : $string('product.charity_donation.read_more_link')
              "
            />
          </div>
        </div>

        <klarna-messaging
          v-if="settings.currencyGbp"
          class="main-product__klarna-messaging"
          namespace="main-product"
          :price="klarnaPrice"
          :variant="variant"
        />

        <div
          class="main-product__description text-body-s-regular rte"
          v-html="product.description || liquid.description"
        />

        <div
          v-if="settings.detailsLink.enabled && detailsContainerLink"
          class="main-product__details-link"
        >
          <a
            :href="detailsContainerLink"
            class="text-body-s-underline"
            v-text="$string('product.show_details')"
          />
        </div>

        <div
          v-if="isMobile && mobileImages"
          class="main-product__mobile-images grid"
        >
          <responsive-image
            v-for="image of mobileImages"
            :key="image.id"
            class="col xs3"
            :image="image.previewImage || image.image || image"
            ratio="186/234"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {String} breadcrumbs - String containing breadcrumbs element.
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} relatedCollection - Object containing collection handle and
 * canvas formatted color related products.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Object} settings - Product page section settings.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
import { mapState } from 'vuex'

import KlarnaMessaging from '~async/klarna-messaging/klarna-messaging'
import ProductColourSelector from '~async/product-colour-selector/product-colour-selector'
import VariantCarousel from '~async/variant-carousel/variant-carousel'

import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import TransitionHeight from '~async/transition-height/transition-height'

import productQuery from '~graphql/product.gql'
import { formatProduct } from '~/helpers/format'
import { emitUpdateRequestObject } from '~/helpers/vue'
import { moveAsyncElement } from '~/helpers/general'

export default {
  name: 'MainProduct',

  components: {
    KlarnaMessaging,
    ProductColourSelector,
    VariantCarousel,
    ProductForm,
    ProductGallery,
    ProductPrices,
    ResponsiveImage,
    TransitionHeight,
  },

  props: {
    breadcrumbs: {
      type: String,
      default: '',
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    relatedCollection: {
      type: Object,
      default: () => ({
        handle: '',
        products: [],
      }),
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    settings: {
      type: Object,
      default: () => ({
        alternateColumns: false,
        backInStock: {},
        charity: {},
        showRnli: {
          type: Boolean,
          default: true,
        },
        currencyGbp: {
          type: Boolean,
          default: false,
        },
        detailsLink: {},
        lowInStock: 0,
        sizeExplainer: '',
        message: {},
        shipping: {},
        stickyProductForm: true,
        modelDetails: '',
        maleModelDetails: '',
        paymentIcons: {},
        showUnitSwitcher: false,
      }),
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      classes: {
        triggerStickyAddToCart: 'product-form--trigger-sticky-add-to-cart',
        showStickyAddToCart: 'product-form__add-to-cart-container--show-sticky-add-to-cart',
      },
      currentIndex: 0,
      currentNamespace: 'gallery',
      handle: cnvs.page.handle,
      state: 'loading',
      showCharityDetails: false,
      klarnaPrice: this.variant.price,
      scroll: {
        direction: 'none',
        isTriggered: false,
        x: 0,
        y: 0,
      },
      timeout: false,
      modelInfo: this.settings.modelDetails,
      activeGender: '',
      isSticky: false,
      abTest: false,
    }
  },

  computed: {

    /**
     *  Returns the dynamically updated value
     */
    computedModelInfo() {
      return this.modelInfo || this.settings.modelDetails;
    },

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      scrollTop: (state) => state.index.scroll.y,
    }),

    /**
     * Compute link to product details.
     * @returns {String | Boolean}
     */
    detailsContainerLink() {
      const detailsContainer = document.querySelector('.product-details')

      if (!detailsContainer) {
        return false
      }

      const containerLink = `#${detailsContainer.id}`
      return containerLink
    },

    /**
     * Compute media items that will be shown only on mobile.
     */
    mobileImages() {
      if (!this.product?.media) {
        return []
      }

      return this.product.media
        .filter(item => item.mediaType === 'image')
        .filter((_image, index) => index === 1 || index === 2)
    },
  },

  watch: {

    /**
     * Watch handle for update to load new product.
     */
    handle() {
      this.queryProduct(this.handle)
    },

    /**
     * Watch scroll to trigger sticky add to cart.
     */
    scrollTop() {
      const productFormElement = this.nodeSelectors.productForm.container

      if (!this.isMobile) {
        return
      }

      // Prevent sticky from firing for out-of-stock products
      if (productFormElement.dataset.stickyAddToCartFeature === 'false') {
        return
      }

      const productFormRect = productFormElement.getBoundingClientRect()
      const productFormBottom = productFormRect.bottom
      const isTriggered = productFormBottom < 0
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight
      const addToCartContainer = this.nodeSelectors.productForm.addToCartContainer

      /**
       * If scroll difference is less than threshold don't update.
       */
      const difference = window.scrollY - this.scroll.y

      if (Math.abs(difference) < this.settings.threshold) {
        return
      }

      this.scroll = {
        direction: difference > 0 ? 'down' : 'up',
        isTriggered,
        x: window.scrollX,
        y: window.scrollY,
      }

      // Check if page is scrolled to the bottom to remove sticky
      if (window.scrollY >= scrollableHeight) {
        addToCartContainer.classList.remove(this.classes.showStickyAddToCart)

        this.timeout = setTimeout(() => {
          productFormElement.classList.remove(this.classes.triggerStickyAddToCart)
        }, this.$timing('slow'))

      // Trigger sticky
      } else if (this.scroll.isTriggered) {
        clearTimeout(this.timeout)
        productFormElement.classList.add(this.classes.triggerStickyAddToCart)
        addToCartContainer.classList.add(this.classes.showStickyAddToCart)

      // Remove sticky
      } else {
        addToCartContainer.classList.remove(this.classes.showStickyAddToCart)

        this.timeout = setTimeout(() => {
          productFormElement.classList.remove(this.classes.triggerStickyAddToCart)
        }, this.$timing('slow'))
      }

      // Check if no variants is selected
      if (!this.variant && !this.variant.available) {
        this.handleAddToCartScroll(productFormRect)
      }
    },
  },

  mounted() {
    this.activeGender = localStorage.activeGender ? localStorage.getItem('activeGender') : 'female'
    this.handleModelInfoUpdate(this.activeGender)

    this.setNodeSelectors()
    this.setProduct()

    moveAsyncElement(
      '[js-yotpo-star-rating="container"]',
      this.$refs.yotpoStarRatingPlaceholder,
    )
  },

  methods: {
    /**
     * Set node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        productForm: {
          container: document.querySelector('[js-product-form="container"]'),
          addToCartContainer: document.querySelector('[js-product-form="addToCartContainer"]'),
          addToCart: document.querySelector('[js-product-form="addToCart"]'),
        },
      }
    },

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      if (!this.liquid.handle) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false || this.product.handle === handle) {
        return
      }

      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country: cnvs.store.country,
            handle,
            language: cnvs.store.language,
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        const formattedProduct = formatProduct(response);

        this.$emit('update-request-object', {
          response: formattedProduct,
          type: 'product',
        })

        this.updateProductUrl(formattedProduct.handle);

        this.state = 'ready'

      } catch (error) {
        cnvs.ReportError('Failed to load product', error)
      }
    },

    /**
     * Update product variant changing variant color.
     * @param {String} handle - New product handle.
     */
    async handleVariantQuery(handle) {
      await this.queryProduct(handle);
    },

    /**
     * Update product URL when loading new handle.
     * - Replaces state so it doesn't affect back navigation.
     * - Use pushState instead to affect back navigation.
     * @param {String} handle - New product handle.
     */
     updateProductUrl(handle) {
      const oldHandle = location.pathname.split('/').reverse()[0];

      if (!oldHandle || oldHandle === handle) {
        return;
      }

      const pathname = location.pathname.replace(oldHandle, handle);
      const url = `${location.origin}${pathname}`;

      history.replaceState(
        {
          html: '',
        },
        '',
        url,
      );
    },

    /**
     * Update model sizing information depending on the current gender.
     * @param {String} newGender - New gender.
     */
    handleModelInfoUpdate(newGender) {
      if (newGender === 'male' && this.settings.maleModelDetails) {
        this.modelInfo = this.settings.maleModelDetails

      } else if (newGender === 'female' && this.settings.modelDetails) {
        this.modelInfo = this.settings.modelDetails

      } else {
        this.modelInfo = ''
      }
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      this.klarnaPrice = variant.price?.amount

      const params = new URLSearchParams(document.location.search.substring(1))

      if (variant?.id) {
        params.set('variant', variant.id)
      }

      const url = variant?.id
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Handle add to cart scroll position when no variant is selected.
     * @param {Object} productFormRect - product form information on size.
     */
    handleAddToCartScroll(productFormRect) {
      this.nodeSelectors.productForm.addToCart.addEventListener('click', () => {
        const siteHeaderHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'))
        const productFormPosition = productFormRect.top + window.scrollY - siteHeaderHeight

        window.scrollTo({
          top: productFormPosition,
          behavior: 'smooth',
        })
      })
    },

    /**
     * Updates main-prduct with new product based on variant selecor
     * @param {string} handle - selected variant handle.
     */
    async handleVariantQuery(handle) {
      await this.queryProduct(handle);
    },
  },
}
</script>

<style lang="scss">
@import '~async/product-gallery-modal/product-gallery-modal-overlay';
@import './main-product';
</style>
